import axios from "axios";

// Create a centralized axios instance for api3.2/blog endpoints with base URL logic
const createCanblogApiInstance = () => {
  const baseURL = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:10000/api3.2/blog'
    : '/api3.2/blog';

  return axios.create({
    baseURL,
    timeout: 30000, // 30 seconds
  });
};

// Get the canblog API instance
const canblogApi = createCanblogApiInstance();

// Helper function to get X-Forwarded-Host header
const getForwardedHost = (apiUrl) => {
  // If apiUrl is missing or empty, use window.location.host
  if (!apiUrl) {
    return window.location.host;
  }

  // For relative URLs (starting with /), use window.location.host directly
  if (apiUrl.startsWith("/")) {
    return window.location.host;
  }

  // For absolute URLs, parse them to extract the host
  try {
    const parsedUrl = new URL(apiUrl);
    // Ensure we always return a valid host, fallback to window.location.host if empty
    return parsedUrl.host || window.location.host;
  } catch (error) {
    // If parsing fails, fallback to window.location.host
    return window.location.host;
  }
};

//=================================POST ENDPOINTS=================================//

/**
 * Get all blog posts with optional filtering
 * GET /api3.2/blog/getAllPosts?status=draft&limit=50&offset=0
 *
 * @param {string} status - Filter by status: 'draft', 'published', or null for all
 * @param {number} limit - Number of posts to return (default: 50)
 * @param {number} offset - Offset for pagination (default: 0)
 * @returns {Promise<Array>} Array of post objects
 */
export const getAllPosts = async (status = null, limit = 50, offset = 0) => {
  try {
    let url = `/getAllPosts?limit=${limit}&offset=${offset}`;
    if (status) {
      url += `&status=${status}`;
    }

    const { data } = await canblogApi.get(url, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canblogApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to fetch posts");
    }

    return data.data || [];
  } catch (error) {
    console.error("[getAllPosts] Failed:", error);
    throw error;
  }
};

/**
 * Get single post by ID
 * GET /api3.2/blog/getPostById?id=123
 *
 * @param {number} id - Post ID
 * @returns {Promise<Object>} Post object
 */
export const getPostById = async (id) => {
  try {
    const { data } = await canblogApi.get(`/getPostById?id=${id}`, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canblogApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to fetch post");
    }

    return data.data;
  } catch (error) {
    console.error("[getPostById] Failed:", error);
    throw error;
  }
};

/**
 * Create new blog post
 * POST /api3.2/blog/createPost
 *
 * @param {Object} postData - Post data object
 * @param {string} postData.title - Post title (required)
 * @param {string} postData.slug - URL slug (required, must be unique)
 * @param {string} postData.content - HTML content (required)
 * @param {string} postData.excerpt - Short description
 * @param {string} postData.featuredImageUrl - Featured image URL
 * @param {string} postData.authorName - Author name
 * @param {number} postData.authorEmployeeId - Author employee ID
 * @param {string} postData.status - 'draft' or 'published'
 * @param {string} postData.publishDate - Publish date (ISO 8601 format)
 * @param {string} postData.metaTitle - SEO title
 * @param {string} postData.metaDescription - SEO description
 * @param {number} postData.categoryId - Category ID
 * @returns {Promise<Object>} Created post object with ID
 */
export const createPost = async (postData) => {
  try {
    const { data } = await canblogApi.post('/createPost', postData, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canblogApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to create post");
    }

    return data.data;
  } catch (error) {
    console.error("[createPost] Failed:", error);
    throw error;
  }
};

/**
 * Update existing blog post
 * POST /api3.2/blog/updatePost
 *
 * @param {Object} postData - Post data object (must include id)
 * @param {number} postData.id - Post ID (required for update)
 * @param {string} postData.title - Post title
 * @param {string} postData.slug - URL slug (must be unique)
 * @param {string} postData.content - HTML content
 * @param {string} postData.excerpt - Short description
 * @param {string} postData.featuredImageUrl - Featured image URL
 * @param {string} postData.authorName - Author name
 * @param {number} postData.authorEmployeeId - Author employee ID
 * @param {string} postData.status - 'draft' or 'published'
 * @param {string} postData.publishDate - Publish date (ISO 8601 format)
 * @param {string} postData.metaTitle - SEO title
 * @param {string} postData.metaDescription - SEO description
 * @param {number} postData.categoryId - Category ID
 * @returns {Promise<Object>} Updated post object
 */
export const updatePost = async (postData) => {
  try {
    const { data } = await canblogApi.post('/updatePost', postData, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canblogApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to update post");
    }

    return data.data;
  } catch (error) {
    console.error("[updatePost] Failed:", error);
    throw error;
  }
};

/**
 * Delete blog post
 * DELETE /api3.2/blog/deletePost?id=123
 *
 * @param {number} id - Post ID
 * @returns {Promise<Object>} Success response
 */
export const deletePost = async (id) => {
  try {
    const { data } = await canblogApi.delete(`/deletePost?id=${id}`, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canblogApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to delete post");
    }

    return data.data;
  } catch (error) {
    console.error("[deletePost] Failed:", error);
    throw error;
  }
};

/**
 * Render a live preview of a post from the editor's current (possibly
 * unsaved / draft) state. Does not persist anything.
 * POST /api3.2/blog/previewPost
 *
 * @param {Object} postData - Same shape as createPost (title, slug, content required)
 * @returns {Promise<string>} Fully rendered blog post HTML document
 */
export const previewPost = async (postData) => {
  try {
    const { data } = await canblogApi.post('/previewPost', postData, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canblogApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to render preview");
    }

    return data.data?.html || "";
  } catch (error) {
    console.error("[previewPost] Failed:", error);
    throw error;
  }
};

//=================================CATEGORY ENDPOINTS=================================//

/**
 * Get all blog categories
 * GET /api3.2/blog/getAllCategories
 *
 * @returns {Promise<Array>} Array of category objects
 */
export const getAllCategories = async () => {
  try {
    const { data } = await canblogApi.get('/getAllCategories', {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canblogApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to fetch categories");
    }

    return data.data || [];
  } catch (error) {
    console.error("[getAllCategories] Failed:", error);
    throw error;
  }
};

/**
 * Create new blog category
 * POST /api3.2/blog/createCategory
 *
 * @param {Object} categoryData - Category data object
 * @param {string} categoryData.categoryName - Category name (required)
 * @param {string} categoryData.slug - URL slug (required)
 * @param {string} categoryData.description - Category description
 * @returns {Promise<Object>} Created category object with ID
 */
export const createCategory = async (categoryData) => {
  try {
    const { data } = await canblogApi.post('/createCategory', categoryData, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canblogApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to create category");
    }

    return data.data;
  } catch (error) {
    console.error("[createCategory] Failed:", error);
    throw error;
  }
};

/**
 * Update existing blog category
 * POST /api3.2/blog/updateCategory
 *
 * @param {Object} categoryData - Category data object
 * @param {number} categoryData.id - Category ID (required)
 * @param {string} categoryData.categoryName - Category name
 * @param {string} categoryData.slug - URL slug
 * @param {string} categoryData.description - Category description
 * @returns {Promise<Object>} Updated category object
 */
export const updateCategory = async (categoryData) => {
  try {
    const { data } = await canblogApi.post('/updateCategory', categoryData, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canblogApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to update category");
    }

    return data.data;
  } catch (error) {
    console.error("[updateCategory] Failed:", error);
    throw error;
  }
};

/**
 * Delete blog category
 * DELETE /api3.2/blog/deleteCategory?id=123
 *
 * @param {number} id - Category ID
 * @returns {Promise<Object>} Success response
 */
export const deleteCategory = async (id) => {
  try {
    const { data } = await canblogApi.delete(`/deleteCategory?id=${id}`, {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "X-Forwarded-Host": getForwardedHost(canblogApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || "Failed to delete category");
    }

    return data.data;
  } catch (error) {
    console.error("[deleteCategory] Failed:", error);
    throw error;
  }
};
